// Copyright (c) StrangeBee 2023

import React, { useEffect, useMemo, useState, ReactElement } from 'react';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { classList } from '@Helpers/className';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { ProviderDrawerProps } from './ProviderDrawerTypes';
import lodash from 'lodash';
import { THPassword } from '@Molecules/Inputs/THPassword/THPassword';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import useValidateForm from '@Hooks/useValidateForm';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { Oauth2, OrganisationOutput, ProfileOutput } from 'thehive-sdk';
import WsConfigForm from '@Organisms/FormConfig/WsConfigForm/WsConfigForm';
import { computeWsConfig } from '@Helpers/wsConfig';

import './ProviderDrawer.scss';

const nameKey = {
    oauth2: {
        clientId: ['clientId'],
        clientSecret: ['clientSecret'],
        redirectUri: ['redirectUri'],
        responseType: ['responseType'],
        grantType: ['grantType'],
        authorizationUrl: ['authorizationUrl'],
        tokenUrl: ['tokenUrl'],
        userUrl: ['userUrl'],
        scope: ['scope'],
        userIdField: ['userIdField'],
        authorizationHeader: ['authorizationHeader'],
    },
    user: {
        autoCreateOnSso: ['user', 'autoCreateOnSso'],
        allowAdminAutoCreate: ['user', 'allowAdminAutoCreate'],
        profileFieldName: ['user', 'profileFieldName'],
        organisationFieldName: ['user', 'organisationFieldName'],
        defaults: {
            profile: ['user', 'defaults', 'profile'],
            organisation: ['user', 'defaults', 'organisation'],
        },
    },
    wsConfig: ['wsConfig'],
};

const resetFields = {
    clientId: '',
    clientSecret: '',
    redirectUri: '',
    grantType: '',
    authorizationUrl: '',
    tokenUrl: '',
    userUrl: '',
    scope: [],
    userIdField: '',
    organisationField: '',
    defaultOrganisation: '',
    authorizationHeader: '',
    wsConfig: {
        proxy: {
            protocol: 'http',
        },
        ssl: {
            loose: {
                checkCertificateAuthority: true,
                acceptAnyCertificate: false,
            },
        },
    },
};

const OauthDrawer = ({
    type,
    title = type,
    enableList,
    getData,
    onSubmit,
    triggerElem,
    availableProviders,
    className,
}: ProviderDrawerProps): ReactElement => {
    const local = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const dataCurrent = useMemo(() => getData(type), [getData, type]);
    const notAvailable = !availableProviders?.includes(type);

    const [initialValues, setInitialValues] = useState<Record<string, any>>({});
    const [organisationOrdered, setOrganisationOrdered] = useState<OrganisationOutput[]>([]);

    const { data: profileList = [] } = useListQuery<ProfileOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listProfile' }],
                name: 'get-all-profiles',
                sort: ['+name'],
            },
        },
        controller: client.ProfileController,
        manual: true,
        fetchOnStart: true,
    });

    const { data: organisationList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listOrganisation' }],
                name: 'get-all-organisations',
                sort: ['+locked', '+name'],
            },
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    const [showForm, setShowForm] = useState<boolean>(false);
    const [showFormAutoCreate, setShowFormAutoCreate] = useState<boolean>(false);

    const [disabledSwitch, setDisabledSwitch] = useState(true);

    const computeOnFinishHandler = (toggleDrawer: () => void) => (formValues: Oauth2) => {
        const result = {
            ...dataCurrent,
            ...formValues,
            UIType: type,
            wsConfig: computeWsConfig(formValues.wsConfig),
        };

        onResetForm();
        toggleDrawer();
        onSubmit(result);
    };

    const onChangeSwitch = (value: any): void => {
        setShowForm(value);
    };
    const onSwitchAutoCreate = (value: any): void => {
        setShowFormAutoCreate(value);
    };

    const onResetForm = () => {
        setFieldsTouched(false);
        initForm(enableList);
        form.resetFields();
    };

    const initForm = (enableList: string[]) => {
        const enabledLocal = enableList.includes(type);
        setShowForm(enabledLocal);
    };

    useEffect(() => {
        const enabledLocal = enableList.includes(type);
        setShowForm(enabledLocal);
        setShowFormAutoCreate(dataCurrent.user.autoCreateOnSso);

        const result = {
            ...resetFields,
            ...lodash.cloneDeep(dataCurrent),
            responseType: 'code',
            ...{ UIType: type, UIenabled: enabledLocal },
        };
        result.wsConfig.ssl.loose.checkCertificateAuthority = !result.wsConfig.ssl.loose.acceptAnyCertificate;

        setInitialValues(result);
    }, [dataCurrent, enableList]); //eslint-disable-line

    useEffect(() => {
        initForm(enableList);
        setDisabledSwitch(!enableList.some((v) => ['local', 'ad', 'ldap', 'sso'].includes(v)));
        // eslint-disable-next-line
    }, [enableList]);

    useEffect(() => {
        if (organisationList) {
            const organisationListTmp = organisationList.sort(({ name: nameA }, { name: nameB }) =>
                nameA.localeCompare(nameB),
            );
            setOrganisationOrdered(organisationListTmp);
        }
    }, [organisationList]);

    useEffect(() => {
        form.resetFields();
    }, [initialValues]); //eslint-disable-line

    return (
        <THDrawer
            title={title}
            className={classList('auth-details__providers-drawer', className)}
            triggerElem={triggerElem}
            disableClose={fieldsTouched}
            afterVisibleChange={() => {
                initForm(enableList);
                form.resetFields();
            }}
        >
            {(toggleDrawer) => (
                <THForm
                    onFinish={computeOnFinishHandler(toggleDrawer)}
                    onValuesChange={() => {
                        setFieldsTouched(true);
                        onFormValuesChange();
                    }}
                    form={form}
                    initialValues={initialValues}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                disabled={!isValid}
                                onCancel={() => {
                                    onResetForm();
                                    toggleDrawer();
                                }}
                                hidden={!fieldsTouched || notAvailable}
                            />
                        }
                    >
                        <THForm.Item
                            name={'UIenabled'}
                            label={local('configPlateform.oauth.enable')}
                            valuePropName="checked"
                        >
                            <THSwitch onChange={onChangeSwitch} disabled={disabledSwitch || notAvailable} />
                        </THForm.Item>
                        {showForm && (
                            <>
                                <div className="auth-form__section">
                                    <TitleSection>{local('configPlateform.general.setting')}</TitleSection>
                                    <THForm.Item
                                        name={nameKey.oauth2.clientId}
                                        label={local('configPlateform.oauth.clientId')}
                                        rules={[{ required: true }]}
                                    >
                                        <THInput disabled={notAvailable} autoComplete="new-password" />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.oauth2.clientSecret}
                                        label={local('configPlateform.oauth.clientSecret')}
                                        rules={[{ required: true }]}
                                    >
                                        <THPassword disabled={notAvailable} autoComplete="new-password" />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.oauth2.redirectUri}
                                        label={local('configPlateform.oauth.redirectUri')}
                                        rules={[{ required: true }]}
                                    >
                                        <THInput disabled={notAvailable} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.oauth2.responseType}
                                        label={local('configPlateform.oauth.responseType')}
                                        rules={[{ required: true }]}
                                    >
                                        <THInput disabled={true} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.oauth2.grantType}
                                        label={local('configPlateform.oauth.grantType')}
                                        rules={[{ required: true }]}
                                    >
                                        <THInput disabled={notAvailable} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.oauth2.authorizationUrl}
                                        label={local('configPlateform.oauth.authorizationUrl')}
                                        rules={[{ required: true }]}
                                    >
                                        <THInput disabled={notAvailable} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.oauth2.authorizationHeader}
                                        label={local('configPlateform.oauth.authorizationHeader')}
                                        rules={[{ required: true }]}
                                    >
                                        <THInput disabled={notAvailable} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.oauth2.tokenUrl}
                                        label={local('configPlateform.oauth.tokenUrl')}
                                        rules={[{ required: true }]}
                                    >
                                        <THInput disabled={notAvailable} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.oauth2.userUrl}
                                        label={local('configPlateform.oauth.userUrl')}
                                        rules={[{ required: true }]}
                                    >
                                        <THInput disabled={notAvailable} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.oauth2.scope}
                                        label={local('configPlateform.oauth.scope')}
                                        rules={[{ required: true }]}
                                    >
                                        <THSelect mode="tags" disabled={notAvailable} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.oauth2.userIdField}
                                        label={local('configPlateform.oauth.userIdField')}
                                        rules={[{ required: true }]}
                                    >
                                        <THInput disabled={notAvailable} />
                                    </THForm.Item>
                                </div>
                                <div className="auth-form__section">
                                    <TitleSection>{local('configPlateform.general.autoCreateSetting')}</TitleSection>
                                    <THForm.Item
                                        name={nameKey.user.autoCreateOnSso}
                                        label={local('configPlateform.oauth.autoCreateOnSso')}
                                        valuePropName="checked"
                                    >
                                        <THSwitch disabled={notAvailable} onChange={onSwitchAutoCreate} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.user.profileFieldName}
                                        label={local('configPlateform.oauth.profileFieldName')}
                                        hidden={!showFormAutoCreate}
                                    >
                                        <THInput disabled={notAvailable} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.user.organisationFieldName}
                                        label={local('configPlateform.oauth.organisationFieldName')}
                                        hidden={!showFormAutoCreate}
                                    >
                                        <THInput disabled={notAvailable} />
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.user.defaults.organisation}
                                        label={local('configPlateform.oauth.defaultSSOOrganisation')}
                                        hidden={!showFormAutoCreate}
                                    >
                                        <THSelect
                                            options={organisationOrdered.map(({ name }) => ({
                                                label: name,
                                                value: name,
                                            }))}
                                        />
                                    </THForm.Item>

                                    <THForm.Item noStyle shouldUpdate>
                                        {({ getFieldValue }) => (
                                            <THForm.Item
                                                name={nameKey.user.defaults.profile}
                                                label={local('configPlateform.oauth.defaultSSOProfile')}
                                                hidden={!showFormAutoCreate}
                                            >
                                                <THSelect
                                                    options={profileList
                                                        .filter((profile) =>
                                                            getFieldValue('user')?.[
                                                                nameKey.user.defaults.organisation[1]
                                                            ]?.[nameKey.user.defaults.organisation[2]] === 'admin'
                                                                ? profile.forAdmin
                                                                : profile.forOrg,
                                                        )
                                                        .map(({ name }) => ({ label: name, value: name }))}
                                                />
                                            </THForm.Item>
                                        )}
                                    </THForm.Item>
                                    <THForm.Item
                                        name={nameKey.user.allowAdminAutoCreate}
                                        label={local('configPlateform.oauth.allowAdminAutoCreate')}
                                        valuePropName="checked"
                                        hidden={!showFormAutoCreate}
                                    >
                                        <THSwitch disabled={notAvailable} />
                                    </THForm.Item>

                                    <WsConfigForm name={nameKey.wsConfig} form={form} />
                                </div>
                            </>
                        )}
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};

export default OauthDrawer;
